import { createSlice } from '@reduxjs/toolkit';
import { isOlderVersion } from 'helpers/timedSales/versionChecker';
import { normalizeBackendDrivenOfferSummaryRow } from 'store/slices/offerSummary/helper/backendDrivenOfferSummary';
import { calculateSummaryFromList, getBidSalesIdDataForWebsockets } from 'store/slices/offerSummary/helper';
import { normalizeLegacyOfferSummaryRow } from 'store/slices/offerSummary/helper/legacyOfferSummary';

const initialState = {
	bidSalesThatUserHasOfferer: [],
	// we memoize this data because when we need to ensure that it doesn't change on every WS message
	bidSalesIdDataForWebsockets: {
		offerSummaryBidSaleIds: [],
		// this is not needed once we turn backend_driven_offer_payload_react on
		offererDealershipIdByBidSaleId: {},
	},
	dealershipsId: [], // logged user dealership_id, or subordinates id,
	isLegacyFormat: true,
	loading: false,
	success: null,
	stats: {
		leaderCount: 0,
		outbiddedCount: 0,
		wonCount: 0,
		lostCount: 0,
		counterCount: 0,
	},
};

const normalizeOfferSummaryRow = ({ offerData, isLegacyFormat }) => {
	return isLegacyFormat ?
			normalizeLegacyOfferSummaryRow(offerData)
		:	normalizeBackendDrivenOfferSummaryRow(offerData);
};

const normalizeOfferSummaryRows = ({ bidSalesArr = [], isLegacyFormat }) => {
	return (bidSalesArr || []).map((bidSale) => normalizeOfferSummaryRow({ offerData: bidSale, isLegacyFormat }));
};

const mergeIncomingOfferSummaryRow = ({ state, incomingChanges, fromUserBid }) => {
	const foundIndex = state.bidSalesThatUserHasOfferer.findIndex(
		(bidSale) => bidSale.bid_sale_id === incomingChanges.bid_sale_id,
	);
	const wasElementFound = foundIndex > -1;

	if (wasElementFound) {
		const elementToUpdate = state.bidSalesThatUserHasOfferer[foundIndex];
		if (
			!elementToUpdate.version ||
			!isOlderVersion({
				incomingChanges: { version: incomingChanges.version },
				storedState: elementToUpdate,
			})
		) {
			state.bidSalesThatUserHasOfferer[foundIndex] = {
				...elementToUpdate,
				...incomingChanges,
			};
		}
	}

	if (!wasElementFound && fromUserBid) {
		state.bidSalesThatUserHasOfferer.push(incomingChanges);
		const { offerSummaryBidSaleIds, offererDealershipIdByBidSaleId } = getBidSalesIdDataForWebsockets({
			bidSalesArr: [incomingChanges],
			existingBidSaleIdsToMerge: state.bidSalesIdDataForWebsockets.offerSummaryBidSaleIds,
			existingDealershipIdByBidSaleIdToMerge: state.bidSalesIdDataForWebsockets.offererDealershipIdByBidSaleId,
		});
		state.bidSalesIdDataForWebsockets.offerSummaryBidSaleIds = offerSummaryBidSaleIds;
		state.bidSalesIdDataForWebsockets.offererDealershipIdByBidSaleId = offererDealershipIdByBidSaleId;
	}
};

const setOfferSummaryRows = ({ state, bidSalesArr }) => {
	const bidSaleById = state.bidSalesThatUserHasOfferer.reduce((acc, bidSale) => {
		acc[bidSale.bid_sale_id] = bidSale;
		return acc;
	}, {});

	const updatedBidSalesArr = bidSalesArr.map((bidSale) => {
		const storedBidSale = bidSaleById[bidSale.bid_sale_id];
		if (storedBidSale && isOlderVersion({ incomingChanges: bidSale, storedState: storedBidSale })) {
			return storedBidSale;
		}
		return bidSale;
	});

	const { offerSummaryBidSaleIds, offererDealershipIdByBidSaleId } = getBidSalesIdDataForWebsockets({
		bidSalesArr: updatedBidSalesArr,
	});
	state.bidSalesIdDataForWebsockets.offerSummaryBidSaleIds = offerSummaryBidSaleIds;
	state.bidSalesIdDataForWebsockets.offererDealershipIdByBidSaleId = offererDealershipIdByBidSaleId;

	state.stats = calculateSummaryFromList({
		bidSalesThatUserHasOfferer: updatedBidSalesArr,
		dealershipsId: state.dealershipsId,
		isLegacyFormat: state.isLegacyFormat,
	});
	state.bidSalesThatUserHasOfferer = updatedBidSalesArr;
	state.loading = false;
	state.success = true;
};

export const buyerOfferSummarySlice = createSlice({
	name: 'buyerOfferSummary',
	initialState,
	reducers: {
		updateOfferSummary: (state, { payload }) => {
			const { fromUserBid } = payload;

			const incomingChanges = normalizeOfferSummaryRow({
				offerData: payload,
				isLegacyFormat: state.isLegacyFormat,
			});
			mergeIncomingOfferSummaryRow({ state, incomingChanges, fromUserBid });

			state.stats = calculateSummaryFromList({
				bidSalesThatUserHasOfferer: state.bidSalesThatUserHasOfferer,
				dealershipsId: state.dealershipsId,
				isLegacyFormat: state.isLegacyFormat,
			});
		},
		removeOneOffer: (state, { payload }) => {
			const { bidSaleId } = payload;
			state.bidSalesThatUserHasOfferer = state.bidSalesThatUserHasOfferer.filter(
				({ bid_sale_id: elementBidSaleID }) => elementBidSaleID !== bidSaleId,
			);
			state.stats = calculateSummaryFromList({
				bidSalesThatUserHasOfferer: state.bidSalesThatUserHasOfferer,
				dealershipsId: state.dealershipsId,
				isLegacyFormat: state.isLegacyFormat,
			});
		},
		setNewBidSalesArray: (state, { payload }) => {
			const { bidSalesArr } = payload;
			const normalizedBidSalesArr = normalizeOfferSummaryRows({
				bidSalesArr,
				isLegacyFormat: state.isLegacyFormat,
			});
			setOfferSummaryRows({ state, bidSalesArr: normalizedBidSalesArr });
		},
		offerSummaryFetchInit: (state, { payload }) => {
			state.dealershipsId = payload.dealershipsId;
			state.isLegacyFormat = payload.isLegacyFormat;
			state.loading = true;
			state.success = null;
		},
		offerSummaryFetchError: (state) => {
			state.loading = false;
			state.success = false;
		},
	},
});

export const {
	updateOfferSummary,
	removeOneOffer,
	setNewBidSalesArray,
	offerSummaryFetchInit,
	offerSummaryFetchError,
} = buyerOfferSummarySlice.actions;

export default buyerOfferSummarySlice.reducer;
